import React from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { isoDateToDateKey } from '../data';
import {
  CalendarViewData,
  HighlightedPeriod,
  HighlightState,
  PlanWeekRoleGroupEntry,
} from '../data/types';

// ─── Constants ────────────────────────────────────────────────────────────────

const COMPLIANCE_COL_ATTR = 'data-sv-compliance-column';
const COMPLIANCE_MOUNT_ATTR = 'data-sv-compliance-mount';
const COMPLIANCE_COL_CLASS = 'sv-compliance-column';
const COMPLIANCE_MOUNT_CLASS = 'sv-compliance-mount';
/** Min/max bounds for the injected Missed column (flex-like sizing between these). */
export const COMPLIANCE_COLUMN_MIN_PX = 100;
export const COMPLIANCE_COLUMN_MAX_PX = 220;
/** Flex ratio: Missed column gets 1 share, each day column gets 1 of 7 shares. */
const PLAN_COLUMN_FLEX = 1;
const DAY_COLUMN_FLEX_TOTAL = 7;

/** Missed-column width from table width — one flex share of eight, clamped to min/max. */
export const computePlanColumnWidth = (tableWidth: number): number => {
  const flexWidth = Math.round(
    (tableWidth * PLAN_COLUMN_FLEX) /
      (DAY_COLUMN_FLEX_TOTAL + PLAN_COLUMN_FLEX),
  );
  return Math.min(
    COMPLIANCE_COLUMN_MAX_PX,
    Math.max(COMPLIANCE_COLUMN_MIN_PX, flexWidth),
  );
};

// ─── DOM types ────────────────────────────────────────────────────────────────

export type PlanWeekMount = {
  weekIndex: number;
  /** Non-padded date key (YYYY-M-D) of the Sunday that starts this row. */
  weekStartDateKey: string | null;
  mountEl: HTMLElement;
};

export type PlanColumnMountPoints = {
  headerMount: HTMLElement | null;
  weeks: PlanWeekMount[];
};

// ─── DOM helpers ─────────────────────────────────────────────────────────────

const getWeekStartDateKeyFromRow = (row: Element | null): string | null => {
  if (!row) return null;
  const firstCell = row.querySelector(
    `td.fc-daygrid-day[data-date]:not(.${COMPLIANCE_COL_CLASS})`,
  );
  const attr = firstCell?.getAttribute('data-date');
  return attr ? isoDateToDateKey(attr) : null;
};

// ─── DOM injection ────────────────────────────────────────────────────────────

const removeComplianceColumn = (calendarEl: HTMLElement): void => {
  calendarEl.querySelectorAll(`[${COMPLIANCE_COL_ATTR}]`).forEach((el) => {
    el.remove();
  });
};

const createMountDiv = (kind: 'header' | 'week'): HTMLDivElement => {
  const mount = document.createElement('div');
  mount.className = COMPLIANCE_MOUNT_CLASS;
  mount.setAttribute(COMPLIANCE_MOUNT_ATTR, kind);
  mount.setAttribute(COMPLIANCE_COL_ATTR, 'true');
  return mount;
};

/**
 * FC month view passes cols:[] so the colgroup is often empty. A lone appended
 * <col> applies to column 1 (Sunday), not column 8 — rebuild all 8 explicitly.
 */
const rebuildColgroup = (
  colgroup: HTMLTableColElement,
  table: HTMLTableElement,
): void => {
  const tableWidth = table.getBoundingClientRect().width;
  if (!tableWidth) return;

  const planColWidth = computePlanColumnWidth(tableWidth);
  const dayColWidth = Math.floor((tableWidth - planColWidth) / 7);

  colgroup.innerHTML = '';

  Array.from({ length: 7 }).forEach(() => {
    const dayCol = document.createElement('col');
    dayCol.style.setProperty('width', `${dayColWidth}px`, 'important');
    colgroup.appendChild(dayCol);
  });

  const complianceCol = document.createElement('col');
  complianceCol.setAttribute(COMPLIANCE_COL_ATTR, 'true');
  complianceCol.className = COMPLIANCE_COL_CLASS;
  complianceCol.style.setProperty('width', `${planColWidth}px`, 'important');
  colgroup.appendChild(complianceCol);
};

const appendComplianceHeaderCell = (headerRow: HTMLTableRowElement): void => {
  const th = document.createElement('th');
  th.setAttribute(COMPLIANCE_COL_ATTR, 'true');
  th.setAttribute('role', 'columnheader');
  th.className = `fc-col-header-cell ${COMPLIANCE_COL_CLASS}`;

  const inner = document.createElement('div');
  inner.className = 'fc-scrollgrid-sync-inner';

  const cushion = document.createElement('span');
  cushion.className = 'fc-col-header-cell-cushion';
  cushion.appendChild(createMountDiv('header'));

  inner.appendChild(cushion);
  th.appendChild(inner);
  headerRow.appendChild(th);
};

const appendComplianceBodyCell = (row: HTMLTableRowElement): void => {
  const td = document.createElement('td');
  td.setAttribute(COMPLIANCE_COL_ATTR, 'true');
  td.setAttribute('role', 'gridcell');
  td.className = `fc-daygrid-day ${COMPLIANCE_COL_CLASS}`;

  const frame = document.createElement('div');
  frame.className = 'fc-daygrid-day-frame fc-scrollgrid-sync-inner';
  frame.appendChild(createMountDiv('week'));
  td.appendChild(frame);
  row.appendChild(td);
};

/**
 * Appends an 8th Missed column to the FullCalendar dayGridMonth view, injecting
 * React mount points inside each header and body cell.
 */
export const injectPlanColumn = (
  calendarEl: HTMLElement | null | undefined,
): void => {
  if (!calendarEl) return;

  const monthView = calendarEl.querySelector('.fc-dayGridMonth-view');
  if (!monthView) return;

  removeComplianceColumn(calendarEl);

  const headerSection = monthView.querySelector(
    '.fc-scrollgrid-section-header',
  );
  if (headerSection) {
    const headerTable = headerSection.querySelector(
      'table',
    ) as HTMLTableElement | null;
    if (headerTable) {
      headerTable.style.tableLayout = 'fixed';
      const colgroup = headerTable.querySelector(
        'colgroup',
      ) as HTMLTableColElement | null;
      if (colgroup) rebuildColgroup(colgroup, headerTable);

      const headerRow = headerTable.querySelector(
        'thead tr',
      ) as HTMLTableRowElement | null;
      if (headerRow) appendComplianceHeaderCell(headerRow);
    }
  }

  const bodySection = monthView.querySelector('.fc-scrollgrid-section-body');
  if (bodySection) {
    const bodyTable = bodySection.querySelector(
      '.fc-daygrid-body table',
    ) as HTMLTableElement | null;
    if (bodyTable) {
      const colgroup = bodyTable.querySelector(
        'colgroup',
      ) as HTMLTableColElement | null;
      if (colgroup) rebuildColgroup(colgroup, bodyTable);

      bodyTable.querySelectorAll('tbody tr').forEach((row) => {
        appendComplianceBodyCell(row as HTMLTableRowElement);
      });
    }
  }
};

/**
 * After injection, collect all mount points and extract the week-start date key
 * so React cells can look themselves up in CalendarViewData.weeks.
 */
export const getPlanColumnMountPoints = (
  calendarEl: HTMLElement | null | undefined,
): PlanColumnMountPoints | null => {
  if (!calendarEl) return null;

  const monthView = calendarEl.querySelector('.fc-dayGridMonth-view');
  if (!monthView) return null;

  const headerMount = monthView.querySelector(
    `[${COMPLIANCE_MOUNT_ATTR}="header"]`,
  ) as HTMLElement | null;

  const weeks: PlanWeekMount[] = Array.from(
    monthView.querySelectorAll(`[${COMPLIANCE_MOUNT_ATTR}="week"]`),
  ).map((mountEl, weekIndex) => ({
    weekIndex,
    weekStartDateKey: getWeekStartDateKeyFromRow(mountEl.closest('tr')),
    mountEl: mountEl as HTMLElement,
  }));

  return { headerMount, weeks };
};

// ─── Styled components ────────────────────────────────────────────────────────

/**
 * "MISSED" in caps, sized to fill most of the column: the header mount is a
 * size container (see WrapperDiv), so 20cqw scales with the column width; the
 * em fallback applies where container units are unsupported.
 */
const HeaderLabel = styled.span`
  display: block;
  font-weight: bold;
  font-size: 1.6em;
  font-size: clamp(14px, 20cqw, 40px);
  line-height: 1.1;
  letter-spacing: 0.02em;
  text-align: center;
  white-space: nowrap;
`;

const WeekCell = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  padding: 2px;
  box-sizing: border-box;
`;

/**
 * Fixed pixel sizes (FullCalendar's cell font is ~10px in the widget). Boxes
 * read "DR 1 of 2", so they keep a minimum square footprint and grow with the
 * label.
 */
const PLAN_BOX_SIZE_PX = 38;
const PLAN_BOX_FONT_PX = 13;
/** Missed boxes: Material red A200, the stakeholder's pick (was red 600 #e53935). */
const MISSED_BOX_COLOR = '#ff5252';

/**
 * Todo boxes keep the theme paper background with a solid 2px text-colour
 * border so they stay legible on light and dark themes; missed boxes are red
 * with a black label for contrast.
 */
const RoleBox = styled.span<{
  $variant: 'missed' | 'todo';
  $highlighted: boolean;
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  min-width: ${PLAN_BOX_SIZE_PX}px;
  height: ${PLAN_BOX_SIZE_PX}px;
  border: solid
    ${(props) =>
      props.$highlighted
        ? props.theme.palette.secondary.main
        : props.theme.palette.text.primary}
    ${(props) => (props.$highlighted ? '3px' : '2px')};
  border-radius: 3px;
  font-size: ${PLAN_BOX_FONT_PX}px;
  font-weight: bold;
  line-height: 1;
  padding: 0 4px;
  text-align: center;
  white-space: nowrap;
  box-sizing: border-box;
  color: ${(props) =>
    props.$variant === 'missed' ? '#000' : props.theme.palette.text.primary};
  cursor: pointer;
  background-color: ${(props) =>
    props.$variant === 'missed'
      ? MISSED_BOX_COLOR
      : props.theme.palette.background.paper};
`;

// ─── React components ─────────────────────────────────────────────────────────

export const PlanColumnHeader = () => <HeaderLabel>MISSED</HeaderLabel>;

/** Box label: "DR 1 of 2" — x visits missed (or still to do) of y planned. */
export const planBoxLabel = (
  roleAbbrev: string,
  count: number,
  planned: number,
): string => `${roleAbbrev} ${count} of ${planned}`;

const pluralVisits = (n: number): string => `visit${n !== 1 ? 's' : ''}`;

type PlanWeekCellProps = {
  weekStartDateKey: string;
  calendarViewData: CalendarViewData;
  filteringRoleGroups: number[];
  highlightState: HighlightState | null;
  onBoxHighlight: (
    cpfId: number,
    weekKey: string,
    periods: HighlightedPeriod[],
  ) => void;
};

const shouldShowEntry = (
  entry: PlanWeekRoleGroupEntry,
  filteringRoleGroups: number[],
): boolean => {
  if (!filteringRoleGroups.length || filteringRoleGroups[0] === 0) return true;
  return filteringRoleGroups.some((id) => String(id) === entry.roleGroupId);
};

export const PlanWeekCell = ({
  weekStartDateKey,
  calendarViewData,
  filteringRoleGroups,
  highlightState,
  onBoxHighlight,
}: PlanWeekCellProps) => {
  const weekData = calendarViewData.weeks[weekStartDateKey];

  if (!weekData) return <WeekCell />;

  return (
    <WeekCell>
      {weekData.missedCounts
        .filter((entry) => shouldShowEntry(entry, filteringRoleGroups))
        .flatMap((entry) => {
          const highlighted =
            highlightState?.cpfId === entry.cpfId &&
            highlightState?.planBoxWeekKey === weekStartDateKey;
          const onClick = () =>
            onBoxHighlight(entry.cpfId, weekStartDateKey, entry.periods);
          const boxes: React.ReactNode[] = [];

          if (entry.todo > 0) {
            boxes.push(
              <RoleBox
                key={`${entry.cpfId}-${entry.roleGroupId}-todo`}
                $variant="todo"
                data-sv-plan-box="todo"
                data-sv-role={entry.roleGroupId}
                data-sv-plan-count={entry.todo}
                data-sv-plan-planned={entry.planned}
                $highlighted={highlighted}
                aria-label={planBoxLabel(
                  entry.roleAbbrev,
                  entry.todo,
                  entry.planned,
                )}
                title={`${entry.todo} of ${
                  entry.planned
                } planned ${pluralVisits(entry.planned)} still to complete`}
                onClick={onClick}
              >
                {planBoxLabel(entry.roleAbbrev, entry.todo, entry.planned)}
              </RoleBox>,
            );
          }

          if (entry.missed > 0) {
            boxes.push(
              <RoleBox
                key={`${entry.cpfId}-${entry.roleGroupId}-missed`}
                $variant="missed"
                data-sv-plan-box="missed"
                data-sv-role={entry.roleGroupId}
                data-sv-plan-count={entry.missed}
                data-sv-plan-planned={entry.planned}
                $highlighted={highlighted}
                aria-label={planBoxLabel(
                  entry.roleAbbrev,
                  entry.missed,
                  entry.planned,
                )}
                title={`${entry.missed} of ${
                  entry.planned
                } planned ${pluralVisits(entry.planned)} missed`}
                onClick={onClick}
              >
                {planBoxLabel(entry.roleAbbrev, entry.missed, entry.planned)}
              </RoleBox>,
            );
          }

          return boxes;
        })}
    </WeekCell>
  );
};

type PlanColumnPortalsProps = {
  mountPoints: PlanColumnMountPoints | null;
  calendarViewData: CalendarViewData | null;
  filteringRoleGroups: number[];
  highlightState: HighlightState | null;
  onBoxHighlight: (
    cpfId: number,
    weekKey: string,
    periods: HighlightedPeriod[],
  ) => void;
};

export const PlanColumnPortals = ({
  mountPoints,
  calendarViewData,
  filteringRoleGroups,
  highlightState,
  onBoxHighlight,
}: PlanColumnPortalsProps) => {
  if (!mountPoints || !calendarViewData) return null;

  return (
    <>
      {mountPoints.headerMount &&
        createPortal(<PlanColumnHeader />, mountPoints.headerMount)}
      {mountPoints.weeks.map(({ weekIndex, weekStartDateKey, mountEl }) => {
        if (!weekStartDateKey) return null;
        return createPortal(
          <PlanWeekCell
            key={weekIndex}
            weekStartDateKey={weekStartDateKey}
            calendarViewData={calendarViewData}
            filteringRoleGroups={filteringRoleGroups}
            highlightState={highlightState}
            onBoxHighlight={onBoxHighlight}
          />,
          mountEl,
        );
      })}
    </>
  );
};
